import { generateThumbnail } from "@features/loras/use-lora-file-input";
import { UploadedPhotoType } from "@features/loras/use-upload-photos";
import { PhotoIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/solid";
import { Button, Spinner, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

// Empty state component
interface EmptyStateProps {
  onUploadClick: () => void;
  loading: boolean;
}

export const EmptyState = ({ onUploadClick, loading }: EmptyStateProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 py-8">
      <PhotoIcon className="h-12 w-12 mx-auto text-gray-400" />
      <div>
        <Text size="4" weight="medium">
          {t("loras.upload.drop_photos")}
        </Text>
        <Text size="2" color="gray" className="block mt-1">
          {t("loras.upload.drop_photos_subtitle")}
        </Text>
      </div>
      <Button variant="solid" onClick={onUploadClick} disabled={loading}>
        <PlusIcon className="h-4 w-4" />
        {t("loras.upload.upload_from_device")}
      </Button>
    </div>
  );
};

// Photo grid component
interface PhotoGridProps {
  goodPhotos: UploadedPhotoType[];
  uploadingPhotos: UploadedPhotoType[];
  onRemove: (photoId: string) => void;
  onUploadClick: () => void;
  loading: boolean;
}

export const PhotoGrid = ({
  goodPhotos,
  uploadingPhotos,
  onRemove,
  onUploadClick,
  loading,
}: PhotoGridProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
        {/* Good photos */}
        {goodPhotos.map((photo) => (
          <PhotoThumbnail
            key={photo.front_id}
            photo={photo}
            onRemove={() => onRemove(photo.front_id)}
          />
        ))}

        {/* Uploading photos */}
        {(uploadingPhotos.length
          ? uploadingPhotos
          : loading
          ? [{ front_id: "loading-1" }]
          : []
        ).map((photo) => (
          <div key={photo.front_id} className="relative aspect-square">
            <div className="w-full h-full bg-gray-200 dark:bg-gray-800 rounded flex items-center justify-center">
              <Spinner size="2" />
            </div>
          </div>
        ))}
      </div>
      <Button variant="outline" onClick={onUploadClick} disabled={loading}>
        <PlusIcon className="h-4 w-4" />
        {t("loras.upload.add_more_photos")}
      </Button>
    </div>
  );
};

// PhotoThumbnail component for displaying uploaded photos
interface PhotoThumbnailProps {
  photo: UploadedPhotoType;
  onRemove: () => void;
  isRejected?: boolean;
}

export const PhotoThumbnail = ({
  photo,
  onRemove,
  isRejected = false,
}: PhotoThumbnailProps) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string>("");

  // Generate thumbnail when component mounts
  useEffect(() => {
    const loadThumbnail = async () => {
      if (photo.url) {
        // Use server URL if available
        try {
          const thumb = await generateThumbnail(photo.url);
          setThumbnailUrl(thumb);
        } catch (e) {
          console.warn("Failed to generate thumbnail:", e);
          setThumbnailUrl(photo.url);
        }
      } else if (photo.front_file) {
        // Fallback: generate thumbnail from original file for rejected photos
        try {
          const fileUrl = URL.createObjectURL(photo.front_file);
          const thumb = await generateThumbnail(fileUrl);
          setThumbnailUrl(thumb);
          // Clean up the object URL
          URL.revokeObjectURL(fileUrl);
        } catch (e) {
          console.warn("Failed to generate thumbnail from file:", e);
          // Final fallback: use object URL directly
          setThumbnailUrl(URL.createObjectURL(photo.front_file));
        }
      }
    };

    loadThumbnail();
  }, [photo.url, photo.front_file]);

  // Cleanup effect to revoke object URLs
  useEffect(() => {
    return () => {
      if (thumbnailUrl && thumbnailUrl.startsWith("blob:")) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [thumbnailUrl]);

  const pending = !photo.id;

  return (
    <div className="relative aspect-square group bg-gray-100 dark:bg-gray-900 rounded overflow-hidden">
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={`preview-${photo.id || photo.front_id}`}
          className={twMerge(
            "w-full aspect-square object-cover rounded",
            pending && "blur",
            isRejected && "opacity-60"
          )}
          onError={(e) => {
            console.warn("Failed to load thumbnail image");
            // Hide broken image
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
          <Spinner size="2" />
        </div>
      )}

      {pending && (
        <Spinner className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
      )}

      {!pending && (
        <button
          onClick={onRemove}
          className="rounded-full bg-red-500 text-white absolute top-1 right-1 p-2 md:p-1 group-hover:block block md:hidden hover:opacity-80"
        >
          <TrashIcon className="h-3 w-3" />
        </button>
      )}

      {photo.front_id}
    </div>
  );
};
